import type { CheckFilter } from '@internal/backstage-plugin-pulse-common';
import { parseEntityRef } from '@backstage/catalog-model';
import { type FilterOption } from './filters';
import { toCanonicalOwnerRef } from './insights/insights-group-scope';

type OwnedDefinition = { ownerEntityRef: string };
type KindScopedDefinition = { filter?: CheckFilter[] };

const fallbackOwnerLabel = (ownerEntityRef: string): string => {
  try {
    return parseEntityRef(ownerEntityRef).name;
  } catch {
    // Keep the raw ref when it cannot be parsed.
    return ownerEntityRef;
  }
};

/**
 * Display label for an owner entity ref. Pass the `labelByRef` map from
 * `useMyTeams` to get the catalog display name instead of the bare
 * entity-ref name.
 */
export const getOwnerLabel = (
  ownerEntityRef: string,
  labelByRef?: Map<string, string>,
): string =>
  labelByRef?.get(toCanonicalOwnerRef(ownerEntityRef)) ??
  fallbackOwnerLabel(ownerEntityRef);

/**
 * Distinct owner options for a set of check/track definitions. Pass the
 * `labelByRef` map from `useMyTeams` to label owners with their catalog
 * display name instead of the bare entity-ref name.
 */
export const getOwnerOptions = (
  definitions: OwnedDefinition[],
  labelByRef?: Map<string, string>,
): FilterOption[] =>
  [...new Set(definitions.map(definition => definition.ownerEntityRef))]
    .map(owner => ({
      value: owner,
      label: getOwnerLabel(owner, labelByRef),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

/** Entity kinds a definition is scoped to via its `filter[].kind` rules. */
export const getAffectedKinds = (
  definition: KindScopedDefinition,
): string[] => {
  const kinds = new Set<string>();
  for (const rule of definition.filter ?? []) {
    for (const kind of rule.kind ?? []) {
      if (kind) {
        kinds.add(kind);
      }
    }
  }
  return [...kinds];
};

/** Distinct "affected entity kind" options across the given definitions. */
export const getAffectedKindOptions = (
  definitions: KindScopedDefinition[],
): FilterOption[] =>
  Array.from(new Set(definitions.flatMap(getAffectedKinds)))
    .sort((a, b) => a.localeCompare(b))
    .map(kind => ({ value: kind, label: kind }));

/**
 * Whether a definition matches the selected entity-kind filter. A definition
 * with no kind restriction applies to all entities, so it matches any selected
 * kind.
 */
export const matchesAffectedKinds = (
  definition: KindScopedDefinition,
  selectedKinds: string[],
): boolean => {
  if (selectedKinds.length === 0) {
    return true;
  }
  const affected = getAffectedKinds(definition);
  if (affected.length === 0) {
    return true;
  }
  const selected = new Set(selectedKinds);
  return affected.some(kind => selected.has(kind));
};
